import React from "react";
import { motion } from "framer-motion";
import {
  CONTACT_EMAIL,
  PHONE_KUWAIT,
  PHONE_UAE,
  WHATSAPP_URL,
} from "../utils/contactInfo";

const Footer = ({ className }) => {
  const currentYear = new Date().getFullYear();

  // Social media links
  const socialLinks = [
    {
      name: "WhatsApp",
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.52 3.48A11.84 11.84 0 0 0 12.08 0C5.58 0 .29 5.29.29 11.79c0 2.08.54 4.11 1.57 5.9L0 24l6.5-1.71a11.74 11.74 0 0 0 5.58 1.42h.01c6.5 0 11.79-5.29 11.79-11.79 0-3.15-1.23-6.1-3.46-8.44zM12.09 21.7h-.01a9.8 9.8 0 0 1-4.99-1.36l-.36-.22-3.86 1.01 1.03-3.76-.24-.39a9.8 9.8 0 0 1-1.5-5.2c0-5.4 4.39-9.79 9.79-9.79a9.7 9.7 0 0 1 6.94 2.88 9.7 9.7 0 0 1 2.86 6.91c0 5.4-4.39 9.79-9.78 9.79zm5.37-7.35c-.29-.14-1.7-.84-1.96-.94-.26-.1-.45-.14-.64.15-.19.29-.74.94-.91 1.13-.17.19-.34.22-.63.07-.29-.14-1.22-.45-2.32-1.45-.86-.77-1.43-1.72-1.6-2.01-.17-.29-.02-.45.13-.59.13-.12.29-.31.43-.46.14-.15.19-.26.29-.43.1-.17.05-.33-.02-.46-.07-.14-.64-1.55-.88-2.12-.23-.56-.46-.48-.64-.49h-.55c-.19 0-.49.07-.74.33-.26.26-.98.96-.98 2.33 0 1.37 1 2.7 1.14 2.89.14.19 1.96 2.99 4.75 4.19.66.29 1.18.47 1.58.6.67.21 1.28.18 1.76.11.54-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.33z" />
        </svg>
      ),
      url: WHATSAPP_URL,
    },
    {
      name: "Email",
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L12 9.573l8.073-6.08c1.618-1.214 3.927-.059 3.927 1.964z" />
        </svg>
      ),
      url: `mailto:${CONTACT_EMAIL}`,
    },
  ];

  // Navigation links for footer
  const navLinks = [
    { name: "Services", href: "/what-we-do" },
    { name: "Contact", href: "/contact-us" },
    { name: "Blog", href: "/blog" },
    { name: "Privacy", href: "/privacy" },
    { name: "Terms", href: "/terms" },
  ];

  return (
    <footer
      id="contact"
      className={`md:min-h-screen relative bg-white/90 backdrop-blur-sm pt-20 sm:pt-24 md:pt-28 lg:pt-36 text-black border-gray-800 overflow-hidden ${className}`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `url('/pictures/pattern.svg') no-repeat center 100px`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            backgroundSize: "cover",
            opacity: 0.1,
            filter: "invert(1) contrast(30%)", // Reduced opacity for better text visibility
          }}
        />
      </div>
      {/* Content Container with higher z-index */}
      <div className="h-full flex justify-between flex-col p-10 md:p-20">
        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-8 sm:gap-x-16 gap-y-8 sm:gap-y-4">
          {/* Company Info */}
          <div className="col-span-full lg:col-span-4 lg:pr-16">
            <motion.h3
              className="text-4xl sm:text-6xl md:text-8xl lg:text-[8rem] xl:text-[10rem] 2xl:text-[12rem] font-bold mb-6 sm:mb-8 md:mb-10 lg:mb-12 logo-font leading-none text-left drop-shadow-lg text-black"
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              Jamil Global
            </motion.h3>
            <motion.p
              className="text-gray-400 mb-8 sm:mb-10 text-base sm:text-lg leading-relaxed text-left"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              Pioneering AI solutions for a smarter, more connected world.
            </motion.p>

            {/* Social Media */}
            <div className="flex justify-start space-x-4 sm:space-x-6 mt-6 sm:mt-8">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white hover:bg-white/20 hover:text-white transition-colors"
                  style={{ backgroundColor: "#112917" }}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1 lg:col-span-1 text-left hidden-on-mobile">
            <motion.h4
              className="text-xl sm:text-2xl font-semibold mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              Quick Links
            </motion.h4>
            <ul className="space-y-3 sm:space-y-4">
              {navLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                >
                  {link.href ? (
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-black transition-colors text-base sm:text-lg"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <span className="text-gray-400 hover:text-black transition-colors text-base sm:text-lg cursor-default">
                      {link.name}
                    </span>
                  )}
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-1 lg:col-span-1 text-left">
            <motion.h4
              className="text-xl sm:text-2xl font-semibold mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              Contact Us
            </motion.h4>
            <motion.address
              className="text-gray-400 not-italic space-y-3 sm:space-y-4 text-base sm:text-lg"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <p>Kuwait City</p>
              <p>Kuwait</p>
              <p className="mt-4 sm:mt-6">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="hover:text-black transition-colors"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                <a
                  href={`tel:${PHONE_KUWAIT.tel}`}
                  className="hover:text-black transition-colors"
                >
                  {PHONE_KUWAIT.display}
                </a>
              </p>
              <p>
                <a
                  href={WHATSAPP_URL}
                  className="hover:text-black transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp {PHONE_UAE.display}
                </a>
              </p>
            </motion.address>
          </div>
        </div>

        <motion.div
          className="border-t border-green-800/20 mt-12 sm:mt-48 lg:mt-[28rem] pt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <p className="text-base sm:text-lg text-black">
            &copy; {currentYear} Jamil Global. All rights reserved.{" "}
            <a href="/privacy" className="underline hover:text-black">
              Privacy
            </a>
            {" · "}
            <a href="/terms" className="underline hover:text-black">
              Terms
            </a>
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
